import React from "react";
import { formatInformationTime } from "@/components/InformationDisplay";
import type { ResearchCalculation, ResearchEvidence, ResearchValidation } from "@shared/researchEvidence";

type AdmittedValue =
  | { kind: "evidence"; value: ResearchEvidence }
  | { kind: "calculation"; value: ResearchCalculation };

function statusLabel(status: ResearchValidation["status"]) {
  if (status === "insufficient_evidence") return "Insufficient evidence";
  return status === "partial" ? "Partial report" : "Report complete";
}

function valueText(value: number, unit: string, currency?: string | null) {
  const displayUnit = unit === "currency per instrument" ? `${currency ?? "Unknown currency"} per instrument` : unit;
  return `${String(value)}${displayUnit ? ` ${displayUnit}` : ""}`;
}

function safeSourceUrl(value: string | null | undefined) {
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.protocol === "https:" || url.protocol === "http:" ? url.toString() : null;
  } catch {
    return null;
  }
}

function sourceState(source: ResearchValidation["input"]["sources"][number]) {
  const { information } = source;
  return `${information.provider.health} · ${information.data.availability} coverage · ${information.data.timeliness} · ${information.data.validity} validity`;
}

export default function ResearchEvidenceDisplay({
  validation,
  fallbackGrounded = false,
}: {
  validation: ResearchValidation;
  fallbackGrounded?: boolean;
}) {
  const admittedIds = new Set(validation.admittedIds);
  const admitted: AdmittedValue[] = [
    ...validation.input.evidence.filter((item) => admittedIds.has(item.id)).map((value) => ({ kind: "evidence" as const, value })),
    ...validation.input.calculations.filter((item) => admittedIds.has(item.id)).map((value) => ({ kind: "calculation" as const, value })),
  ];
  const grounded = fallbackGrounded || validation.segments.some((segment) => segment.grounded);
  const sourceWarnings = validation.input.sources.filter(({ information }) =>
    information.provider.health !== "healthy" || information.data.availability !== "available" ||
    ["delayed", "older-observation", "unknown"].includes(information.data.timeliness) || information.data.validity !== "valid",
  );
  const exclusions = [...new Map(
    [...validation.input.excluded, ...validation.excluded].map((item) => [
      `${item.provider}\u0000${item.id ?? item.label}\u0000${item.reason}`,
      item,
    ]),
  ).values()];
  const admittedLabels = new Map(admitted.map(({ value }) => [value.id, "metricLabel" in value ? value.metricLabel : value.label]));

  return (
    <section className="analytics-source-block" aria-label="Research evidence coverage">
      <h3>Evidence coverage</h3>
      <p>
        Report status: <strong>{statusLabel(validation.status)}</strong> · {admitted.length} admitted value{admitted.length === 1 ? "" : "s"}
      </p>
      <p>
        Evidence cutoff: {formatInformationTime(validation.input.cutoff)} · Finalized: {formatInformationTime(validation.finalizedAt)}
      </p>

      <h4>Admitted evidence</h4>
      {admitted.length ? (
        <ul>
          {admitted.map(({ kind, value }) => kind === "evidence" ? (
            <li key={value.id}>
              <strong>{value.metricLabel}</strong> ({value.instrument}): {valueText(value.value, value.unit, value.currency)}
              {" · "}{value.provider} · {value.clock}: {formatInformationTime(value.observedAt)}
              {value.sourceUrl && safeSourceUrl(value.sourceUrl) && (
                <> · <a href={safeSourceUrl(value.sourceUrl)!} target="_blank" rel="noopener noreferrer">Source</a></>
              )}
            </li>
          ) : (
            <li key={value.id}>
              <strong>{value.label}</strong>: {valueText(value.value, value.unit)} · calculated from {value.inputIds.map((id) => admittedLabels.get(id) ?? "admitted value").join(" and ")} · {formatInformationTime(value.observedAt)}
            </li>
          ))}
        </ul>
      ) : <p>No values remain admitted at finalization.</p>}

      <h4>Source and provider states</h4>
      {validation.input.sources.length ? (
        <ul>
          {validation.input.sources.map((source, index) => (
            <li key={`${source.provider}-${source.label}-${index}`}>
              {source.label} ({source.provider}): {sourceState(source)}
              {sourceWarnings.includes(source) && <strong> · Warning</strong>}
              {source.information.provider.failure && <> · {source.information.provider.failure.message}</>}
            </li>
          ))}
        </ul>
      ) : <p>No provider states were returned.</p>}

      <h4>Excluded evidence</h4>
      {exclusions.length ? (
        <ul>
          {exclusions.map((item, index) => (
            <li key={`${item.id ?? "excluded"}-${item.provider}-${item.label}-${index}`}>
              {item.label} ({item.provider}){item.metric ? ` · ${item.metric}` : ""}: {item.reason}
            </li>
          ))}
        </ul>
      ) : <p>No exclusions were reported.</p>}

      {validation.reasonCodes.length > 0 && <p>Result notes: {validation.reasonCodes.join(" · ")}</p>}
      {grounded && (
        <p>
          Grounding metadata records linked search sources. It does not verify factual claims.
        </p>
      )}
    </section>
  );
}
